import { useCallback, useMemo } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { DEFAULT_SETTINGS, DEFAULT_CATEGORIES, STORAGE_KEYS } from '../../utils/constants';
import {
  SAMPLE_BUDGET_ITEMS, SAMPLE_BUCKETS, SAMPLE_YEARLY_EXPENSES,
  SAMPLE_TRANSACTIONS, SAMPLE_RECURRING_TEMPLATES,
} from '../../utils/sampleData';
import { uuid } from '../../utils/formatting';
import { computeBalanceCostBasis } from '../../utils/calculations';
import { DATA_VERSION, applyPayload } from '../../utils/migrate';

/**
 * Owns all persistent data state and domain mutation helpers.
 * Returns a memoized object so the BudgetProvider's context value memo
 * only re-runs when actual data changes.
 */
export function useBudgetData({ showToast }) {
  // ─── Persistent state ────────────────────────────────────────────────────────
  const [settings,           setSettings]           = useLocalStorage(STORAGE_KEYS.settings,           DEFAULT_SETTINGS);
  const [categories,         setCategories]         = useLocalStorage(STORAGE_KEYS.categories,         DEFAULT_CATEGORIES);
  const [budgetItems,        setBudgetItems]        = useLocalStorage(STORAGE_KEYS.budgetItems,        []);
  const [transactions,       setTransactions]       = useLocalStorage(STORAGE_KEYS.transactions,       []);
  const [buckets,            setBuckets]            = useLocalStorage(STORAGE_KEYS.buckets,            []);
  const [yearlyExpenses,     setYearlyExpenses]     = useLocalStorage(STORAGE_KEYS.yearlyExp,          []);
  const [currencyBalances,   setCurrencyBalances]   = useLocalStorage(STORAGE_KEYS.currencyBalances,   []);
  const [recurringTemplates, setRecurringTemplates] = useLocalStorage(STORAGE_KEYS.recurringTemplates, []);

  // Derived payload — single source of truth for file/drive sync and JSON export
  const savePayload = useMemo(() => ({
    version: DATA_VERSION,
    settings, categories, budgetItems, transactions,
    buckets, yearlyExpenses, currencyBalances, recurringTemplates,
  }), [settings, categories, budgetItems, transactions,
       buckets, yearlyExpenses, currencyBalances, recurringTemplates]);

  // ─── Named helpers (replaces raw setSettings exposure) ──────────────────────
  const updateSettings = useCallback((s) => setSettings(s), [setSettings]);

  // ─── Category helpers ────────────────────────────────────────────────────────
  const addCategory = useCallback((cat) => {
    const id = `cat_${uuid()}`;
    setCategories(prev => [...prev, { ...cat, id }]);
    setBudgetItems(prev => [...prev, { id, amount: 0 }]);
    return id;
  }, [setCategories, setBudgetItems]);

  const deleteCategory = useCallback((id) => {
    setCategories(prev => prev.filter(c => c.id !== id));
    setBudgetItems(prev => prev.filter(b => b.id !== id));
  }, [setCategories, setBudgetItems]);

  const updateCategory = useCallback((id, changes) => {
    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...changes } : c));
  }, [setCategories]);

  const updateBudgetItem = useCallback((id, amount, month = null) => {
    setBudgetItems(prev => {
      const exists = prev.find(b => b.id === id);
      if (month) {
        if (exists) return prev.map(b => b.id === id ? { ...b, overrides: { ...(b.overrides || {}), [month]: amount } } : b);
        return [...prev, { id, amount: 0, overrides: { [month]: amount } }];
      }
      if (exists) return prev.map(b => b.id === id ? { ...b, amount } : b);
      return [...prev, { id, amount }];
    });
  }, [setBudgetItems]);

  // Set the default budget for a category starting from fromMonth forward.
  // Past months retain the previous default via history entries.
  const setDefaultFrom = useCallback((id, amount, fromMonth) => {
    setBudgetItems(prev => {
      const exists = prev.find(b => b.id === id);
      if (exists) {
        return prev.map(b => {
          if (b.id !== id) return b;
          const history = [...(b.history || [])];
          const idx = history.findIndex(h => h.from === fromMonth);
          if (idx >= 0) history[idx] = { from: fromMonth, amount };
          else history.push({ from: fromMonth, amount });
          history.sort((a, b) => a.from.localeCompare(b.from));
          const overrides = { ...(b.overrides || {}) };
          delete overrides[fromMonth];
          return { ...b, history, overrides };
        });
      }
      return [...prev, { id, amount: 0, history: [{ from: fromMonth, amount }] }];
    });
  }, [setBudgetItems]);

  const clearBudgetOverride = useCallback((id, month) => {
    setBudgetItems(prev => prev.map(b => {
      if (b.id !== id) return b;
      const overrides = { ...(b.overrides || {}) };
      delete overrides[month];
      return { ...b, overrides };
    }));
  }, [setBudgetItems]);

  // ─── Transaction helpers ──────────────────────────────────────────────────────
  // A balance spend is a projection of its source transaction, never a separate
  // untracked record. Keeping the source id lets edit/delete stay consistent.
  function spendForTransaction(txn, balances = []) {
    if (!txn || txn.fromBalance === false || !Number.isFinite(Number(txn.foreignAmount)) || Number(txn.foreignAmount) <= 0) return null;
    let balanceId = txn.currencyBalanceId;
    if (!balanceId && txn.currency) {
      const match = balances.find(b => b.currency === txn.currency);
      if (match) balanceId = match.id;
    }
    if (!balanceId) return null;
    return {
      id: uuid(),
      transactionId: txn.id,
      foreignAmount: Number(txn.foreignAmount),
      amount: txn.amount,
      rate: txn.exchangeRate,
      date: txn.date,
      description: txn.description,
    };
  }

  function isSpendForTransaction(spend, txn) {
    if (!txn || !txn.id) return false;
    if (spend.transactionId === txn.id) return true;
    if (!spend.transactionId && spend.date === txn.date && spend.description === txn.description && Number(spend.foreignAmount) === Number(txn.foreignAmount)) {
      return true;
    }
    return false;
  }

  function syncTransactionSpend(balances, previousTxn, nextTxn) {
    const cleanedBalances = (previousTxn && previousTxn.id)
      ? balances.map(balance => ({
          ...balance,
          spends: (balance.spends || []).filter(spend => !isSpendForTransaction(spend, previousTxn)),
        }))
      : balances;

    const spend = spendForTransaction(nextTxn, cleanedBalances);
    if (!spend) return cleanedBalances;

    const targetBalanceId = nextTxn?.currencyBalanceId || cleanedBalances.find(b => b.currency === nextTxn?.currency)?.id;
    if (!targetBalanceId) return cleanedBalances;

    return cleanedBalances.map(balance =>
      balance.id === targetBalanceId
        ? { ...balance, spends: [...(balance.spends || []), spend] }
        : balance
    );
  }

  const addTransaction = useCallback((txn) => {
    const full = { ...txn, id: uuid() };
    setTransactions(prev => [...prev, full]);
    setCurrencyBalances(prev => syncTransactionSpend(prev, null, full));
    return full;
  }, [setTransactions, setCurrencyBalances]);

  const addTransactions = useCallback((txns) => {
    const full = txns.map(t => ({ ...t, id: uuid() }));
    setTransactions(prev => [...prev, ...full]);
    setCurrencyBalances(prev => full.reduce((balances, txn) => syncTransactionSpend(balances, null, txn), prev));
    return full;
  }, [setTransactions, setCurrencyBalances]);

  const updateTransaction = useCallback((id, updates) => {
    setTransactions(prev => {
      const previousTxn = prev.find(t => t.id === id);
      if (!previousTxn) return prev;
      const nextTxn = { ...previousTxn, ...updates, id };
      setCurrencyBalances(cbPrev => syncTransactionSpend(cbPrev, previousTxn, nextTxn));
      return prev.map(t => t.id === id ? nextTxn : t);
    });
  }, [setTransactions, setCurrencyBalances]);

  const deleteTransaction = useCallback((id) => {
    setTransactions(prev => {
      const previousTxn = prev.find(t => t.id === id);
      if (previousTxn) {
        setCurrencyBalances(cbPrev => syncTransactionSpend(cbPrev, previousTxn, null));
      }
      return prev.filter(t => t.id !== id);
    });
  }, [setTransactions, setCurrencyBalances]);

  // ─── Bucket helpers ───────────────────────────────────────────────────────────
  const addBucket = useCallback((bucket) => {
    const id = `bkt_${uuid()}`;
    const full = { ...bucket, id, adjustments: [], createdDate: new Date().toISOString().split('T')[0] };
    setBuckets(prev => [...prev, full]);
    return id;
  }, [setBuckets]);

  const updateBucket = useCallback((id, updates) => {
    setBuckets(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
  }, [setBuckets]);

  const deleteBucket = useCallback((id) => {
    setBuckets(prev => prev.filter(b => b.id !== id));
  }, [setBuckets]);

  const addBucketAdjustment = useCallback((bucketId, adj) => {
    const full = { ...adj, id: uuid() };
    setBuckets(prev => prev.map(b =>
      b.id === bucketId ? { ...b, adjustments: [...(b.adjustments || []), full] } : b
    ));
  }, [setBuckets]);

  const deleteAdjustment = useCallback((bucketId, adjId) => {
    setBuckets(prev => prev.map(b =>
      b.id === bucketId ? { ...b, adjustments: (b.adjustments || []).filter(a => a.id !== adjId) } : b
    ));
  }, [setBuckets]);

  const updateAdjustment = useCallback((bucketId, adjId, updates) => {
    setBuckets(prev => prev.map(b =>
      b.id === bucketId
        ? { ...b, adjustments: (b.adjustments || []).map(a => a.id === adjId ? { ...a, ...updates } : a) }
        : b
    ));
  }, [setBuckets]);

  // ─── Currency balance helpers ─────────────────────────────────────────────────
  const addCurrencyBalance = useCallback((data) => {
    const id = `cbal_${uuid()}`;
    setCurrencyBalances(prev => [...prev, { ...data, id, lots: [] }]);
    return id;
  }, [setCurrencyBalances]);

  const deleteCurrencyBalance = useCallback((id) => {
    setCurrencyBalances(prev => prev.filter(b => b.id !== id));
  }, [setCurrencyBalances]);

  const updateCurrencyBalance = useCallback((id, updates) => {
    setCurrencyBalances(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
  }, [setCurrencyBalances]);

  const addCurrencyLot = useCallback((balanceId, lotData) => {
    const lot = { ...lotData, id: uuid() };
    setCurrencyBalances(prev => prev.map(b =>
      b.id === balanceId ? { ...b, lots: [...(b.lots || []), lot] } : b
    ));
  }, [setCurrencyBalances]);

  const deleteCurrencyLot = useCallback((balanceId, lotId) => {
    setCurrencyBalances(prev => prev.map(b =>
      b.id === balanceId ? { ...b, lots: (b.lots || []).filter(l => l.id !== lotId) } : b
    ));
  }, [setCurrencyBalances]);

  const updateCurrencyLot = useCallback((balanceId, lotId, updates) => {
    setCurrencyBalances(prev => prev.map(b =>
      b.id === balanceId
        ? { ...b, lots: (b.lots || []).map(l => l.id === lotId ? { ...l, ...updates } : l) }
        : b
    ));
  }, [setCurrencyBalances]);

  // Record a spend from a foreign currency balance (append-only — does not mutate lots)
  const deductFromCurrencyBalance = useCallback((balanceId, spend) => {
    setCurrencyBalances(prev => prev.map(b => {
      if (b.id !== balanceId) return b;
      const entry = { ...spend, id: uuid() };
      return { ...b, spends: [...(b.spends || []), entry] };
    }));
  }, [setCurrencyBalances]);

  // Sell foreignAmount at sellRate — deducts from balance, returns realized info.
  // Computes availability and caps the sell amount synchronously (before calling the
  // state setter) so the return value is always accurate. The state setter only handles
  // the lot reduction; it reads the pre-computed sellAmt via closure.
  // Uses costBasis.totalForeign (lots minus spends) as the available denominator.
  // The fraction is applied to raw lot totals so proportional reduction stays correct:
  //   new_lots = raw_lots × (1 - sellAmt/rawTotal)
  //   new_available = new_lots - spends = available - sellAmt ✓
  const sellCurrencyBalance = useCallback((balanceId, foreignAmount, sellRate) => {
    const balance = currencyBalances.find(b => b.id === balanceId);
    if (!balance) return { foreignAmount: 0, sellRate, avgRate: 0, nisReceived: 0, nisAtAvg: 0, gainLoss: 0 };

    const costBasis = computeBalanceCostBasis(balance);
    const available = costBasis.totalForeign; // lots minus already-recorded spends
    const avgRate   = costBasis.avgRate;

    if (available <= 0) return { foreignAmount: 0, sellRate, avgRate: 0, nisReceived: 0, nisAtAvg: 0, gainLoss: 0 };

    const sellAmt = Math.min(foreignAmount, available); // cap to prevent oversell

    setCurrencyBalances(prev => prev.map(b => {
      if (b.id !== balanceId) return b;
      const rawTotal = (b.lots || []).reduce((s, l) => s + l.foreignAmount, 0);
      if (rawTotal <= 0) return b;
      const fraction = sellAmt / rawTotal;
      const updatedLots = (b.lots || [])
        .map(l => ({ ...l, foreignAmount: parseFloat((l.foreignAmount * (1 - fraction)).toFixed(6)) }))
        .filter(l => l.foreignAmount > 0.001);
      return { ...b, lots: updatedLots };
    }));

    return {
      foreignAmount: sellAmt,
      sellRate,
      avgRate,
      nisReceived: parseFloat((sellAmt * sellRate).toFixed(2)),
      nisAtAvg:    parseFloat((sellAmt * avgRate).toFixed(2)),
      gainLoss:    parseFloat((sellAmt * (sellRate - avgRate)).toFixed(2)),
    };
  }, [currencyBalances, setCurrencyBalances]);

  // ─── Recurring templates ──────────────────────────────────────────────────────
  // Template shape: { id, label, type, category, description, amount, bucket,
  //                   frequency: 'monthly'|'weekly', dayOfMonth, lastAdded }
  const addRecurringTemplate = useCallback((template) => {
    const id = `rec_${uuid()}`;
    setRecurringTemplates(prev => [...prev, { ...template, id, lastAdded: null }]);
    return id;
  }, [setRecurringTemplates]);

  const updateRecurringTemplate = useCallback((id, updates) => {
    setRecurringTemplates(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, [setRecurringTemplates]);

  const deleteRecurringTemplate = useCallback((id) => {
    setRecurringTemplates(prev => prev.filter(t => t.id !== id));
  }, [setRecurringTemplates]);

  // ─── Yearly expenses ──────────────────────────────────────────────────────────
  const addYearlyExpense = useCallback((exp) => {
    const id = `yexp_${uuid()}`;
    setYearlyExpenses(prev => [...prev, { ...exp, id }]);
    return id;
  }, [setYearlyExpenses]);

  const updateYearlyExpense = useCallback((id, updates) => {
    setYearlyExpenses(prev => prev.map(y => y.id === id ? { ...y, ...updates } : y));
  }, [setYearlyExpenses]);

  const deleteYearlyExpense = useCallback((id) => {
    setYearlyExpenses(prev => prev.filter(y => y.id !== id));
  }, [setYearlyExpenses]);

  // ─── Sample data ──────────────────────────────────────────────────────────────
  const loadSampleData = useCallback(() => {
    setCategories(DEFAULT_CATEGORIES);
    setBudgetItems(SAMPLE_BUDGET_ITEMS);
    setTransactions(SAMPLE_TRANSACTIONS);
    setBuckets(SAMPLE_BUCKETS);
    setYearlyExpenses(SAMPLE_YEARLY_EXPENSES);
    setRecurringTemplates(SAMPLE_RECURRING_TEMPLATES);
    showToast('Sample data loaded');
  }, [setCategories, setBudgetItems, setTransactions, setBuckets,
      setYearlyExpenses, setRecurringTemplates, showToast]);

  // ─── Export / Import ──────────────────────────────────────────────────────────
  const exportJSON = useCallback(() => {
    const blob = new Blob([JSON.stringify(savePayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budget_backup_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [savePayload]);

  const importJSON = useCallback((jsonStr) => {
    try {
      const data = JSON.parse(jsonStr);
      applyPayload(data, {
        setSettings, setCategories, setBudgetItems, setTransactions,
        setBuckets, setYearlyExpenses, setCurrencyBalances, setRecurringTemplates,
      });
      showToast('Data imported successfully');
    } catch (e) {
      const detail = e instanceof SyntaxError ? 'not valid JSON' : e.message;
      showToast(`Invalid backup file: ${detail}`, 'error');
    }
  }, [setSettings, setCategories, setBudgetItems, setTransactions, setBuckets,
      setYearlyExpenses, setCurrencyBalances, setRecurringTemplates, showToast]);

  const exportCSV = useCallback(() => {
    const rows = [
      ['Date', 'Type', 'Category', 'Description', 'Amount'],
      ...transactions.map(t => {
        const cat = categories.find(c => c.id === t.category);
        return [t.date, t.type, cat ? cat.name : t.category, t.description, t.amount];
      }),
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [transactions, categories]);

  // ─── Memoized return object ────────────────────────────────────────────────────
  // All helpers are useCallback so their refs are stable — this memo only fires
  // when actual state data changes, not on every provider render.
  return useMemo(() => ({
    // State
    settings, categories, budgetItems, transactions,
    buckets, yearlyExpenses, currencyBalances, recurringTemplates,
    // Raw setters — consumed by sync hooks (useFileSync/useDriveSync) only
    setSettings, setCategories, setBudgetItems, setTransactions,
    setBuckets, setYearlyExpenses, setCurrencyBalances, setRecurringTemplates,
    // Derived
    savePayload,
    // Named helpers (preferred over raw setters for external callers)
    updateSettings,
    // Category
    addCategory, deleteCategory, updateCategory,
    updateBudgetItem, setDefaultFrom, clearBudgetOverride,
    // Transaction
    addTransaction, addTransactions, updateTransaction, deleteTransaction,
    // Bucket
    addBucket, updateBucket, deleteBucket, addBucketAdjustment, deleteAdjustment, updateAdjustment,
    // Currency
    addCurrencyBalance, deleteCurrencyBalance, updateCurrencyBalance,
    addCurrencyLot, deleteCurrencyLot, updateCurrencyLot,
    deductFromCurrencyBalance, sellCurrencyBalance,
    // Recurring templates
    addRecurringTemplate, updateRecurringTemplate, deleteRecurringTemplate,
    // Yearly expenses
    addYearlyExpense, updateYearlyExpense, deleteYearlyExpense,
    // Export / Import / Sample
    exportJSON, importJSON, exportCSV, loadSampleData,
  }), [
    settings, categories, budgetItems, transactions,
    buckets, yearlyExpenses, currencyBalances, recurringTemplates,
    setSettings, setCategories, setBudgetItems, setTransactions,
    setBuckets, setYearlyExpenses, setCurrencyBalances, setRecurringTemplates,
    savePayload, updateSettings,
    addCategory, deleteCategory, updateCategory,
    updateBudgetItem, setDefaultFrom, clearBudgetOverride,
    addTransaction, addTransactions, updateTransaction, deleteTransaction,
    addBucket, updateBucket, deleteBucket, addBucketAdjustment, deleteAdjustment, updateAdjustment,
    addCurrencyBalance, deleteCurrencyBalance, updateCurrencyBalance,
    addCurrencyLot, deleteCurrencyLot, updateCurrencyLot,
    deductFromCurrencyBalance, sellCurrencyBalance,
    addRecurringTemplate, updateRecurringTemplate, deleteRecurringTemplate,
    addYearlyExpense, updateYearlyExpense, deleteYearlyExpense,
    exportJSON, importJSON, exportCSV, loadSampleData,
  ]);
}
